import { useState } from "react";
import { Image } from "expo-image";
import { StyleSheet, Text, View } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import {
  fonts,
  palette,
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

export type CustomMapMarker = {
  id: string;
  label: string;
  /** Normalised 0..1 so placements survive the map being re-uploaded. */
  x: number;
  y: number;
  selected?: boolean;
};

const MIN_SCALE = 1;
const MAX_SCALE = 6;

export function CustomMap({
  imageUri,
  aspectRatio = 4 / 3,
  markers,
  placing = false,
  onPressMap,
  onPressMarker,
}: {
  imageUri: string;
  aspectRatio?: number;
  markers: CustomMapMarker[];
  placing?: boolean;
  onPressMap?: (point: { x: number; y: number }) => void;
  onPressMarker?: (markerId: string) => void;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const [container, setContainer] = useState({ width: 0, height: 0 });
  const scale = useSharedValue(1);
  const savedScale = useSharedValue(1);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const savedTranslateX = useSharedValue(0);
  const savedTranslateY = useSharedValue(0);

  // Fit the drawing inside the container while preserving its aspect ratio, so
  // a pin at (0.5, 0.5) always lands in the middle of the artwork.
  const canvas =
    container.width / container.height > aspectRatio
      ? { width: container.height * aspectRatio, height: container.height }
      : { width: container.width, height: container.width / aspectRatio };

  const pan = Gesture.Pan()
    .onUpdate((event) => {
      translateX.value = savedTranslateX.value + event.translationX;
      translateY.value = savedTranslateY.value + event.translationY;
    })
    .onEnd(() => {
      savedTranslateX.value = translateX.value;
      savedTranslateY.value = translateY.value;
    });

  const pinch = Gesture.Pinch()
    .onUpdate((event) => {
      scale.value = Math.min(
        Math.max(savedScale.value * event.scale, MIN_SCALE),
        MAX_SCALE,
      );
    })
    .onEnd(() => {
      savedScale.value = scale.value;
      if (scale.value <= MIN_SCALE) {
        translateX.value = withTiming(0);
        translateY.value = withTiming(0);
        savedTranslateX.value = 0;
        savedTranslateY.value = 0;
      }
    });

  const tap = Gesture.Tap().onEnd((event) => {
    if (!placing || !onPressMap || canvas.width === 0) return;
    onPressMap({
      x: Math.min(Math.max(event.x / canvas.width, 0), 1),
      y: Math.min(Math.max(event.y / canvas.height, 0), 1),
    });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
      { scale: scale.value },
    ],
  }));

  return (
    <View
      style={stylesheet.container}
      onLayout={(event) =>
        setContainer({
          width: event.nativeEvent.layout.width,
          height: event.nativeEvent.layout.height,
        })
      }
    >
      {container.width > 0 ? (
        <GestureDetector gesture={Gesture.Simultaneous(pan, pinch, tap)}>
          <Animated.View
            style={[
              { width: canvas.width, height: canvas.height },
              animatedStyle,
            ]}
          >
            <Image
              source={{ uri: imageUri }}
              style={StyleSheet.absoluteFill}
              contentFit="contain"
              accessibilityLabel="Collection map"
            />
            {markers.map((marker) => (
              <View
                key={marker.id}
                accessibilityRole="button"
                accessibilityLabel={marker.label}
                onTouchEnd={() => onPressMarker?.(marker.id)}
                style={[
                  stylesheet.pin,
                  marker.selected ? stylesheet.pinSelected : null,
                  {
                    left: marker.x * canvas.width,
                    top: marker.y * canvas.height,
                  },
                ]}
              >
                <Text
                  numberOfLines={1}
                  style={[
                    stylesheet.pinText,
                    marker.selected ? stylesheet.pinTextSelected : null,
                  ]}
                >
                  {marker.label}
                </Text>
              </View>
            ))}
          </Animated.View>
        </GestureDetector>
      ) : null}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      minHeight: 320,
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      borderRadius: radii.card,
      backgroundColor: colors.surface,
    },
    pin: {
      position: "absolute",
      maxWidth: 140,
      paddingHorizontal: spacing.s,
      paddingVertical: 4,
      borderWidth: 2,
      borderColor: colors.background,
      borderRadius: radii.pill,
      backgroundColor: colors.primary,
      transform: [{ translateX: -20 }, { translateY: -14 }],
    },
    pinSelected: { backgroundColor: palette.brandMarigold },
    pinText: {
      color: "#FFFFFF",
      fontFamily: fonts.medium,
      fontSize: 10,
    },
    pinTextSelected: {
      color: palette.brandMidnight,
    },
  });
